import { createFileRoute, Link } from '@tanstack/react-router'
import { LegalDocShell, LegalSectionCard } from '#bv/components/LegalDocShell'
import { PulseDigital_VOUCHES_CHANNEL, PulseDigital_VOUCHES_CHANNEL_URL } from '#bv/lib/replacementPolicy'

function VouchChannelLink(props: { className?: string }) {
  return (
    <a
      href={PulseDigital_VOUCHES_CHANNEL_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={props.className ?? 'font-medium text-[#a5d8ff] underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#93c5fd]'}
    >
      {PulseDigital_VOUCHES_CHANNEL}
    </a>
  )
}

export const Route = createFileRoute('/terms')({
  component: TermsPage,
})

function TermsPage() {
  return (
    <LegalDocShell
      eyebrow="Legal"
      title="TERMS OF SERVICE"
      lastUpdated="April 2026"
      intro={
        <>
          Purchasing or using PulseDigital means you accept these conditions. Read them carefully before you buy. For
          refunds and replacements, see our{' '}
          <Link to="/refund" className="font-medium text-[#a5d8ff] underline decoration-[#00bfff]/40 underline-offset-2 transition-colors hover:text-[#93c5fd]">
            refund policy
          </Link>
          .
        </>
      }
    >
      <div className="space-y-8 sm:space-y-10">
        <LegalSectionCard title="At a glance">
          <ul className="list-none space-y-3 pl-0">
            {[
              {
                k: 'No cash refunds by default',
                t: 'We replace eligible issues where policy allows; see below.',
              },
              {
                k: 'Replacements are time-limited',
                t: (
                  <>
                    Post your vouch in <VouchChannelLink /> within 24 hours of delivery to activate warranty. Your window runs from that vouch (see §2). Report dead-on-arrival issues in your ticket before vouching.
                  </>
                ),
              },
              {
                k: 'Proof may be required',
                t: 'Screenshots, screen recordings, or other evidence as we request.',
              },
            ].map((row) => (
              <li key={row.k} className="flex gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
                <span className="mt-0.5 h-5 w-1 shrink-0 rounded-full bg-gradient-to-b from-[#00bfff] to-[#00bfff]/40" aria-hidden />
                <span>
                  <span className="font-semibold text-[#a5d8ff]">{row.k}</span>
                  <span className="text-[#a8c4e8]"> — {row.t}</span>
                </span>
              </li>
            ))}
          </ul>
        </LegalSectionCard>

        <LegalSectionCard title="Refund & replacement information">
          <ul className="space-y-2.5 text-[#a8c4e8] [&_strong]:text-[#f0f6ff]">
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              All transactions are treated as <strong>final</strong> unless we explicitly state otherwise for a specific promotion or error on our side.
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              We do not refund for change of mind, buyer&apos;s remorse, or misunderstanding of what was purchased. Make sure you understand the product listing before checkout.
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              To be eligible for a <strong>replacement</strong>, you must activate warranty by posting in our Discord{' '}
              <VouchChannelLink className="font-semibold text-[#f0f6ff] underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#93c5fd]" />{' '}
              channel within <strong>24 hours of staff delivery</strong> (or automated access). Your replacement window{' '}
              <strong>starts when you vouch</strong> — often <strong>24 hours from that vouch</strong> for general listings; shorter or longer periods are in §2 or on the product page.{' '}
              <strong>Dead on arrival:</strong> if the product does not work on first use, reply in your support ticket <strong>before</strong> vouching. See our{' '}
              <Link to="/refund" className="font-medium text-[#a5d8ff] underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#93c5fd]">
                refund policy
              </Link>{' '}
              for steps.
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              <strong>Cash refunds</strong> may be granted only when the issue is clearly <strong>our responsibility</strong> (for example, verified non-delivery or a duplicate charge caused by our systems). Proof (e.g. screenshots or recordings) is usually required.
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              All claims are reviewed before approval. Refunds are not issued for user error, misuse, or circumstances outside our control (including delays by payment processors, platforms, or third parties).
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              Repeated abuse of support or chargebacks may result in restricted access or blacklist from future purchases.
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              Accounts or access tied to our services may be <strong>closed or revoked</strong> if they violate platform terms, our rules, or show abusive behaviour. In those cases, replacement or refund is generally not available.
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              If you believe your case qualifies, open a ticket in our Discord within your product&apos;s replacement window — see{' '}
              <a href="https://discord.gg/tGxhDfRkdE" target="_blank" rel="noopener noreferrer" className="font-medium text-[#a5d8ff] underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#93c5fd]">
                Discord
              </a>
              .
            </li>
          </ul>
        </LegalSectionCard>

        <LegalSectionCard badge="1" title="Refunds">
          <p className="text-[#8ecfff]">
            By default: <strong className="text-[#f0f6ff]">no cash refunds</strong> — only replacements or resolutions where our policy and your product&apos;s warranty allow. Exceptions are rare and require documentation.
          </p>
        </LegalSectionCard>

        <LegalSectionCard badge="2" title="Warranty & support windows (product-specific)">
          <p className="mb-4 text-[#5a6d8a]">
            <strong className="text-[#a5d8ff]">Vouch to activate.</strong> After we deliver (ticket message or instant access), post in{' '}
            <VouchChannelLink className="text-[#f0f6ff] underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#93c5fd]" /> within 24 hours with your order ID and product name. Your warranty clock starts at that vouch post — not at checkout. Include{' '}
            <strong className="text-[#f0f6ff]">Order ID</strong>, <strong className="text-[#f0f6ff]">product</strong>, and confirm it is working. If it failed immediately, use your ticket first (DOA) — do not vouch a broken delivery.
          </p>
          <p className="mb-6 text-[#5a6d8a]">
            The table below is the replacement window <strong className="text-[#f0f6ff]">from your vouch</strong> unless the product page says otherwise.
          </p>
          <div className="overflow-hidden rounded-xl border border-white/[0.1] bg-[rgba(7,5,10,0.6)] shadow-inner">
            <table className="w-full text-left text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.06]">
                  <th className="px-4 py-3.5 font-semibold tracking-wide" style={{ color: '#a5d8ff' }}>
                    Category / product type
                  </th>
                  <th className="px-4 py-3.5 font-semibold tracking-wide" style={{ color: '#a5d8ff' }}>
                    Typical window (from vouch)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {[
                  ['Spotify-related SKUs', `3 days from your ${PulseDigital_VOUCHES_CHANNEL} post`],
                  ['Minecraft / similar account SKUs', `3 days from your ${PulseDigital_VOUCHES_CHANNEL} post`],
                  ['YouTube / Prime Video / Canva / CapCut Pro FA / ChatGPT / Apple Music (FA-style accounts)', `7 days from your ${PulseDigital_VOUCHES_CHANNEL} post`],
                  ['Gemini Pro + Google AI PRO (e.g. 2TB bundles)', '3 days from vouch — one replacement per order only'],
                  ['Discord Nitro promo / token-style SKUs', 'Usually 1 hour from vouch — check the product page'],
                  ['Other digital goods', '24 hours from vouch unless the product page states otherwise'],
                ].map(([a, b]) => (
                  <tr key={String(a)} className="transition-colors even:bg-white/[0.02] hover:bg-white/[0.05]">
                    <td className="px-4 py-3 align-top text-[#a8c4e8]">{a}</td>
                    <td className="px-4 py-3 align-top font-medium text-[#f0f6ff]/95">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 rounded-xl border border-[rgba(0, 191, 255,0.15)] bg-[rgba(0, 191, 255,0.06)] px-4 py-3 text-xs leading-relaxed text-[#ddb8d4] sm:text-sm">
            <span className="font-semibold text-[#a5d8ff]">Gemini Pro + Google AI PRO:</span> each order includes at most one replacement within the window above; after that, no further replacements or refunds apply under this warranty for that purchase.
          </p>
        </LegalSectionCard>

        <LegalSectionCard badge="3" title="Verification for replacements">
          <ul className="space-y-2.5 text-[#a8c4e8]">
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              Vouches must include a valid order ID and match the delivered product. We may deny warranty if you did not vouch within 24 hours of delivery, vouched before testing, or vouched for a product that was already reported broken in-ticket.
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              For codes, promos, tokens, or keys, we may require a recording or screenshots showing the code as delivered and evidence it failed when used correctly.
            </li>
            <li className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
              If your payment account (e.g. PayPal) is limited or restricted after purchase for reasons outside our control, we generally cannot refund or replace on that basis alone.
            </li>
          </ul>
        </LegalSectionCard>

        <LegalSectionCard badge="4" title="Usage & conduct">
          <ul className="space-y-2.5 text-[#a8c4e8]">
            {[
              'Industry-wide revokes or waves (e.g. mass Nitro or Spotify revokes) may be outside our control — warranty may not apply in those cases.',
              'If you change credentials on a "full access" (FA) account against instructions, replacement rights may be void.',
              'If a supplier fails us, we will do our best to help, but we cannot guarantee refunds beyond what our policy allows.',
              'Staff or partner perks may be revoked for repeated violations of team rules, as stated when those perks are granted.',
              'If payment rails we use are disrupted or blocked through no fault of yours alone, resolution follows our policy and what we can legally and practically offer.',
              '"Supplier contact" or similar informational listings may carry no warranty unless explicitly stated.',
              'Purchasing means you accept these terms in full.',
            ].map((t) => (
              <li key={t} className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
                {t}
              </li>
            ))}
          </ul>
        </LegalSectionCard>

        <LegalSectionCard badge="5" title="Limitations & educational use">
          <p className="text-[#8ecfff]">
            Digital goods are provided for lawful use only. You are responsible for compliance with third-party platforms&apos; rules and applicable law. We are not responsible for buyer misuse or illegal use.
          </p>
        </LegalSectionCard>

        <LegalSectionCard badge="6" title="Changes to these terms">
          <p className="text-[#8ecfff]">
            We may update these terms at any time. The &quot;Last updated&quot; date will change when we do. Continuing to browse or purchase means you accept the latest version.
          </p>
        </LegalSectionCard>

        <LegalSectionCard badge="7" title="Takedown requests (rights holders)">
          <p className="text-[#8ecfff]">
            If you operate a service we reference and believe a listing should be removed or corrected, contact us through{' '}
            <Link to="/contact" className="font-medium text-[#a5d8ff] underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#93c5fd]">
              our contact page
            </Link>{' '}
            with details. We review legitimate requests in good faith.
          </p>
        </LegalSectionCard>

        <LegalSectionCard badge="8" title="Acceptance, products, eligibility & payments">
          <ul className="space-y-2.5 text-[#a8c4e8]">
            {[
              'By using PulseDigital you agree to these terms. If you disagree, do not purchase.',
              'We sell digital goods as described on each product page. Third-party platforms have their own rules; we are not endorsed by them.',
              'You must meet the legal age to contract in your jurisdiction.',
              'Payments may be processed via MoneyMotion or other methods we enable. Chargebacks without contacting support first may result in restrictions.',
            ].map((t) => (
              <li key={t} className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
                {t}
              </li>
            ))}
          </ul>
        </LegalSectionCard>

        <LegalSectionCard badge="9" title="Delivery, liability & disputes">
          <ul className="space-y-2.5 text-[#a8c4e8]">
            {[
              'Delivery is digital; wrong email addresses provided by you may delay or prevent delivery.',
              'Liability is limited to the amount paid for the affected order where permitted by law.',
              'Disputes: prefer contacting us first; we may use arbitration or other processes as described in prior versions of these terms where applicable.',
            ].map((t) => (
              <li key={t} className="relative pl-4 before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#00bfff]/70">
                {t}
              </li>
            ))}
          </ul>
        </LegalSectionCard>

        <LegalSectionCard badge="10" title="Contact">
          <p className="flex flex-wrap gap-x-3 gap-y-2 text-[#8ecfff]">
            <Link to="/contact" className="btn-link rounded-lg px-3 py-1.5 text-sm">
              Contact
            </Link>
            <Link to="/refund" className="btn-link rounded-lg px-3 py-1.5 text-sm">
              Refund policy
            </Link>
            <a
              href="https://discord.gg/tGxhDfRkdE"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-discord rounded-lg px-3 py-1.5 text-sm"
            >
              Discord
            </a>
          </p>
        </LegalSectionCard>

        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-5 py-5 sm:px-6">
          <p className="text-xs leading-relaxed" style={{ color: '#6b5a66' }}>
            PulseDigital · These terms are provided for clarity and do not constitute legal advice. For specific legal questions, consult a professional in your jurisdiction.
          </p>
        </div>
      </div>
    </LegalDocShell>
  )
}
